import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { ROLES, GOALS } from "../../data";
import { ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", role: "", goal: "", message: "" };

export const Booking = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.role || !form.goal) {
      toast.error("Please fill in your name, email, role and goal.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Request received. Angelo will reach out shortly.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-black border-white/15 text-white h-12 rounded-lg focus-visible:ring-2 focus-visible:ring-[#e5ff00] focus-visible:ring-offset-0 focus-visible:border-[#e5ff00] placeholder:text-white/30";

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative bg-[#050505] py-24 md:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <span className="text-xs uppercase tracking-[0.3em] text-[#e5ff00]">
                Apply
              </span>
              <h2 className="mt-5 font-display uppercase text-4xl md:text-6xl lg:text-7xl leading-[0.86]">
                Start your
                <br />
                <span className="text-[#e5ff00]">recomp</span>
              </h2>
              <p className="mt-7 text-white/60 leading-relaxed max-w-md">
                Tell me about your shift and your goals. I'll build the plan
                around your real life — no cookie-cutter templates, no 9-to-5
                rules.
              </p>
              <div className="mt-10 space-y-4">
                {[
                  "Free intro consultation",
                  "Custom shift-based programming",
                  "Direct access to Angelo",
                ].map((t) => (
                  <div key={t} className="flex items-center gap-3 text-white/70">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#e5ff00]" />
                    <span className="text-sm uppercase tracking-[0.12em]">
                      {t}
                    </span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form
                data-testid="booking-form"
                onSubmit={submit}
                className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 md:p-10"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">
                      Name *
                    </label>
                    <Input
                      data-testid="booking-name"
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                      placeholder="Your name"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">
                      Email *
                    </label>
                    <Input
                      data-testid="booking-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      placeholder="you@email.com"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">
                      Phone
                    </label>
                    <Input
                      data-testid="booking-phone"
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value)}
                      placeholder="(702) 000-0000"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">
                      Your Role *
                    </label>
                    <Select value={form.role} onValueChange={(v) => set("role", v)}>
                      <SelectTrigger
                        data-testid="booking-role"
                        className={inputCls}
                      >
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/15 text-white">
                        {ROLES.map((r) => (
                          <SelectItem key={r} value={r} className="focus:bg-white/10 focus:text-white">
                            {r}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-5">
                  <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">
                    Primary Goal *
                  </label>
                  <Select value={form.goal} onValueChange={(v) => set("goal", v)}>
                    <SelectTrigger data-testid="booking-goal" className={inputCls}>
                      <SelectValue placeholder="What are you after?" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/15 text-white">
                      {GOALS.map((g) => (
                        <SelectItem key={g} value={g} className="focus:bg-white/10 focus:text-white">
                          {g}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="mt-5">
                  <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">
                    Tell me about your shift
                  </label>
                  <Textarea
                    data-testid="booking-message"
                    value={form.message}
                    onChange={(e) => set("message", e.target.value)}
                    placeholder="Hours, current habits, what you've tried..."
                    className={`${inputCls} h-32 py-3 resize-none`}
                  />
                </div>

                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  data-testid="booking-submit"
                  type="submit"
                  disabled={loading}
                  className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-[#e5ff00] text-black uppercase tracking-[0.2em] text-sm font-bold h-14 rounded-full hover:neon-glow transition-shadow duration-300 disabled:opacity-60"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <>
                      Send Application
                      <ArrowRight size={18} />
                    </>
                  )}
                </motion.button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
